import React from "react";
import { cn } from "@gettactic/helpers/src/css/cn";
import { CheckIcon } from "@cmp-src/icons/icons";

type Props = {
  currentStep: string;
};

type Step = {
  id: number;
  path: string;
  label: string;
  completedLabel?: string;
};

const STEPS: Step[] = [
  { id: 1, path: "/setup/organization", label: "Create Organization" },
  {
    id: 2,
    path: "/setup/offices",
    label: "Add Buildings",
    completedLabel: "Buildings Added"
  },
  {
    id: 3,
    path: "/setup/teams",
    label: "Set Up Teams",
    completedLabel: "Teams Set Up"
  },
  {
    id: 4,
    path: "/setup/resources",
    label: "Create Workspaces",
    completedLabel: "Workspaces Created"
  },
  { id: 5, path: "/setup/users", label: "Invite Users" }
];

const StepSeparator = () => (
  <div
    className="absolute top-0 right-0 hidden h-full w-5 md:block"
    aria-hidden="true"
  >
    <svg
      className="h-full w-full text-neutral-300"
      viewBox="0 0 22 80"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d="M0 -2L20 40L0 82"
        vectorEffect="non-scaling-stroke"
        stroke="currentcolor"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const CurrentStepCircle: React.FC<{ number: string }> = ({ number }) => (
  <div className="relative h-10 w-10 shrink-0">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 78 78"
      fill="none"
      className="absolute inset-0 scale-125"
    >
      <circle
        cx="39"
        cy="39"
        r="37"
        stroke="url(#paint0_linear_1447_58310)"
        strokeWidth="4"
      />
      <defs>
        <linearGradient
          id="paint0_linear_1447_58310"
          x1="39"
          y1="0"
          x2="39"
          y2="78"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4984E4" />
          <stop offset="1" stopColor="#A954DA" />
        </linearGradient>
      </defs>
    </svg>
    <span className="rainbow-text absolute inset-0 flex items-center justify-center font-bold text-[16px]">
      {number}
    </span>
  </div>
);

export const SetupNavBar: React.FC<Props> = ({ currentStep }) => {
  const currentIndex = Math.max(
    0,
    STEPS.findIndex((s) => s.path === currentStep)
  );

  return (
    <nav aria-label="Progress" className="bg-white">
      <ol className="divide-y divide-neutral-300 rounded-md border border-neutral-300 md:flex md:divide-y-0">
        {STEPS.map((step, i) => {
          const isCurrent = step.path === currentStep;
          const isCompleted = i < currentIndex;
          const isUpcoming = !isCurrent && !isCompleted;

          let numberBadge: React.ReactNode;
          if (isCurrent) {
            numberBadge = (
              <CurrentStepCircle number={String(step.id).padStart(2, "0")} />
            );
          } else if (isCompleted) {
            numberBadge = (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F2449]">
                <CheckIcon className="size-6 text-white" />
              </span>
            );
          } else {
            numberBadge = (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-neutral-300">
                <span className="text-neutral-500">
                  {String(step.id).padStart(2, "0")}
                </span>
              </span>
            );
          }

          const labelText =
            isCompleted && step.completedLabel
              ? step.completedLabel
              : step.label;
          const labelClasses = cn(
            "ml-4 text-[16px]",
            isCurrent
              ? "font-bold rainbow-text"
              : isUpcoming
                ? "text-neutral-500"
                : "text-neutral-900"
          );

          return (
            <li
              key={step.id}
              className={cn(
                isCurrent ? "" : "hidden",
                "relative md:flex md:flex-1"
              )}
            >
              <div className="group flex w-full items-center px-6 py-4 font-medium text-sm">
                {numberBadge}
                <span className={labelClasses}>{labelText}</span>
              </div>
              {i < STEPS.length - 1 && <StepSeparator />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
